import React, { useState, useEffect } from 'react';
import { Users, Briefcase, Map, Star, UserPlus, MapPin, Trash2, Map as MapIcon, ArrowRight, Car, ClipboardList, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import ReactECharts from 'echarts-for-react';

const COLORS = ['#e63946', '#f4a261', '#e9c46a', '#2a9d8f', '#264653', '#1d3557', '#457b9d', '#a8dadc', '#8338ec', '#ff006e', '#fb5607', '#ffbe0b'];

export default function Customers() {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const navigate = useNavigate();
  const routesLibrary = useMapsLibrary('routes');

  const [customers, setCustomers] = useState([]);
  const [savedPlaces, setSavedPlaces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  
  // Data for summary
  const [pieData, setPieData] = useState([]);
  const [teamPieData, setTeamPieData] = useState([]);
  const [salesRepPieData, setSalesRepPieData] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [uniqueSegments, setUniqueSegments] = useState([]);
  const [uniqueIndustries, setUniqueIndustries] = useState([]);

  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    fetch('/api/users')
      .then(res => res.json())
      .then(userData => {
        const usersList = userData.status === 'success' ? userData.data : [];
        setAllUsers(usersList);
        fetchCustomers(usersList);
      })
      .catch(err => {
        console.error(err);
        fetchCustomers([]);
      });
      
    fetchSavedPlaces();
  }, []);

  const fetchSavedPlaces = async () => {
    try {
      const res = await fetch(`/api/google_places?email=${user?.google_email || ''}`);
      const data = await res.json();
      if (data.status === 'success') {
        setSavedPlaces(data.data);
      }
    } catch (err) {
      console.error("Error fetching saved places", err);
    }
  };

  const fetchCustomers = async (usersData) => {
    try {
      const res = await fetch('/api/customers');
      const data = await res.json();
      if (data.status === 'success') {
        let cData = data.data;

        // Build mapping
        const salesCodeToTeam = {};
        usersData.forEach(u => {
          if (u.sales_code) {
            salesCodeToTeam[u.sales_code] = u.team_name;
          }
        });
        
        // Filter by user team if not admin
        if (user && user.role !== 'admin' && user.role !== 'super_admin' && user.team_name) {
          cData = cData.filter(c => 
            salesCodeToTeam[c.update_sales_code] === user.team_name || 
            salesCodeToTeam[c.update_after_sales_code] === user.team_name
          );
        }
        
        setCustomers(cData);
        processData(cData, salesCodeToTeam);
      }
    } catch (err) {
      console.error("Error fetching customers", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePlace = async (id) => {
    if (!window.confirm("คุณต้องการลบข้อมูลนี้ใช่หรือไม่?")) return;
    try {
      const res = await fetch('/api/google_places/' + id, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchSavedPlaces();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdatePlace = async (id, action) => {
    try {
      const res = await fetch('/api/google_places/' + id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            id: id, 
            action: action,
            user_email: user?.google_email
        })
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchSavedPlaces();
        if (action === 'plan_visit') {
          alert('เพิ่มลงในรายการงานของฉันแล้ว!');
        }
      } else {
        alert('Error: ' + data.message);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleShowOnMap = (place) => {
    navigate('/', { 
      state: { 
        searchResult: { 
          type: 'google', 
          data: { place_id: place.place_id } 
        } 
      } 
    });
  };

  const processData = (data, salesCodeToTeam) => {
    setTotalCustomers(data.length);
    
    // Count unique segments and industries
    const segments = new Set();
    const industries = new Set();
    
    const industryMap = {};
    const teamMap = {};
    const repMap = {};
    
    data.forEach(c => {
      if (c.segment) segments.add(c.segment);
      if (c.industry) industries.add(c.industry);
      
      const industryName = c.industry && c.industry.trim() !== '' ? c.industry : 'Others';
      if (!industryMap[industryName]) {
        industryMap[industryName] = 0;
      }
      industryMap[industryName]++;
      
      const mappedTeam = c.update_sales_code && salesCodeToTeam[c.update_sales_code] ? salesCodeToTeam[c.update_sales_code] : null;
      const teamName = mappedTeam ? mappedTeam : 'Unassigned';
      if (!teamMap[teamName]) {
        teamMap[teamName] = 0;
      }
      teamMap[teamName]++;
      
      const repName = c.update_sales_code && c.update_sales_code.trim() !== '' ? c.update_sales_code : 'Unassigned';
      if (!repMap[repName]) {
        repMap[repName] = 0;
      }
      repMap[repName]++;
    });
    
    setUniqueSegments(Array.from(segments).filter(s => s && s.trim() !== ''));
    setUniqueIndustries(Array.from(industries).filter(i => i && i.trim() !== ''));
    
    // Convert industryMap to array for Recharts and sort by value descending
    const chartData = Object.keys(industryMap).map(key => ({
      name: key,
      value: industryMap[key]
    })).sort((a, b) => b.value - a.value);
    
    // If there are too many small groups, combine them into 'Others'
    let finalChartData = [];
    let othersCount = 0;
    
    chartData.forEach((item, index) => {
      if (index < 10) { // Keep top 10 groups
        finalChartData.push(item);
      } else {
        othersCount += item.value;
      }
    });
    
    if (othersCount > 0) {
      const existingOthersIndex = finalChartData.findIndex(item => item.name === 'Others');
      if (existingOthersIndex !== -1) {
        finalChartData[existingOthersIndex].value += othersCount;
      } else {
        finalChartData.push({ name: 'Others', value: othersCount });
      }
    }
    
    setPieData(finalChartData);
    
    // Convert teamMap to array for Recharts and sort by value descending
    const teamChartData = Object.keys(teamMap).map(key => ({
      name: key,
      value: teamMap[key]
    })).sort((a, b) => b.value - a.value);
    
    // Group small teams into 'Others' if needed (top 10)
    let finalTeamChartData = [];
    let othersTeamCount = 0;
    
    teamChartData.forEach((item, index) => {
      if (index < 10) {
        finalTeamChartData.push(item);
      } else {
        othersTeamCount += item.value;
      }
    });
    
    if (othersTeamCount > 0) {
      finalTeamChartData.push({ name: 'Others', value: othersTeamCount });
    }
    
    setTeamPieData(finalTeamChartData);
    
    // Convert repMap to array for Recharts
    const repChartData = Object.keys(repMap).map(key => ({
      name: key,
      value: repMap[key]
    })).sort((a, b) => b.value - a.value);
    
    let finalRepChartData = [];
    let othersRepCount = 0;
    
    repChartData.forEach((item, index) => {
      if (index < 10) {
        finalRepChartData.push(item);
      } else {
        othersRepCount += item.value;
      }
    });
    
    if (othersRepCount > 0) {
      finalRepChartData.push({ name: 'Others', value: othersRepCount });
    }
    
    setSalesRepPieData(finalRepChartData);
  };

  const filteredCustomers = customers.filter(c => {
    const searchStr = searchTerm.toLowerCase();
    return (
      (c.cd_no && c.cd_no.toLowerCase().includes(searchStr)) ||
      (c.search_name && c.search_name.toLowerCase().includes(searchStr)) ||
      (c.customer_group && c.customer_group.toLowerCase().includes(searchStr))
    );
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner" style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #c8102e', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
      </div>
    );
  }

  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');
  const secondChartData = isAdmin ? teamPieData : salesRepPieData;
  const secondChartTitle = isAdmin ? 'Sales Team Distribution' : 'Sales Rep Distribution';

  const favoritePlaces = savedPlaces.filter(p => p.type === 'favorite' && p.is_planned != 1);
  const newCustomers = savedPlaces.filter(p => p.type === 'new_customer' && p.is_planned != 1);
  const plannedTasks = savedPlaces.filter(p => p.is_planned == 1);

  return (
    <div style={{ padding: 0, display: 'flex', flexDirection: 'column', gap: '1.5rem', overflowX: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '1.8rem', color: '#1f2937' }}>ข้อมูลลูกค้า (Customer Dashboard)</h1>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
        <div className="card" style={{ display: 'flex', alignItems: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: '#eff6ff', padding: '0.75rem', borderRadius: '12px', marginRight: '0.75rem' }}>
            <Users size={24} color="#2563eb" />
          </div>
          <div>
            <h3 style={{ margin: '0 0 0.25rem 0', color: '#6b7280', fontSize: '0.8rem', fontWeight: 600 }}>Total Customers</h3>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1f2937' }}>{totalCustomers.toLocaleString()}</div>
          </div>
        </div>
        
        <div className="card" style={{ display: 'flex', alignItems: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: '#fef3c7', padding: '0.75rem', borderRadius: '12px', marginRight: '0.75rem', alignSelf: 'flex-start' }}>
            <Map size={24} color="#d97706" />
          </div>
          <div>
            <h3 style={{ margin: '0 0 0.25rem 0', color: '#6b7280', fontSize: '0.8rem', fontWeight: 600 }}>Unique Segments</h3>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1f2937' }}>{uniqueSegments.length.toLocaleString()}</div>
            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
              {uniqueSegments.map((seg, idx) => (
                <span key={idx} style={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', padding: '2px 4px', borderRadius: '4px' }}>{seg}</span>
              ))}
            </div>
          </div>
        </div>
        
        <div className="card" style={{ display: 'flex', alignItems: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: '#f3e8ff', padding: '0.75rem', borderRadius: '12px', marginRight: '0.75rem', alignSelf: 'flex-start' }}>
            <Briefcase size={24} color="#9333ea" />
          </div>
          <div>
            <h3 style={{ margin: '0 0 0.25rem 0', color: '#6b7280', fontSize: '0.8rem', fontWeight: 600 }}>Industries</h3>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1f2937' }}>{uniqueIndustries.length.toLocaleString()}</div>
            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
              {uniqueIndustries.map((ind, idx) => (
                <span key={idx} style={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', padding: '2px 4px', borderRadius: '4px' }}>{ind}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: '1.5rem' }}>

        <div className="card" style={{ padding: '1rem' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.1rem', fontWeight: 700 }}>{secondChartTitle}</h3>
          <div style={{ width: '100%', height: 350 }}>
            <ReactECharts 
              option={{
                tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
                color: [...COLORS.slice(4), ...COLORS.slice(0, 4)], // Offset colors
                series: [
                  {
                    type: 'pie',
                    radius: ['45%', '70%'],
                    avoidLabelOverlap: true,
                    itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
                    label: { show: true, formatter: '{b}\n{c} ({d}%)', color: '#475569', fontSize: 11, lineHeight: 14 },
                    labelLine: { show: true, smooth: 0.2, length: 15, length2: 15 },
                    data: secondChartData
                  }
                ]
              }}
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="card" style={{ padding: '1rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.2rem', fontWeight: 700 }}>Customer List</h3>
          <input 
            type="text" 
            placeholder="Search CD_No, Name, Group..." 
            className="input-field"
            style={{ width: '100%', maxWidth: '300px' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div style={{ overflowX: 'auto', maxHeight: '500px', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f1f5f9', zIndex: 1 }}>
              <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                <th style={{ padding: '0.75rem 1rem' }}>CD_No</th>
                <th style={{ padding: '0.75rem 1rem' }}>Group</th>
                <th style={{ padding: '0.75rem 1rem' }}>Search Name</th>
                <th style={{ padding: '0.75rem 1rem' }}>Customer Name</th>
                <th style={{ padding: '0.75rem 1rem' }}>Sales Code</th>
                <th style={{ padding: '0.75rem 1rem' }}>After Sales Code</th>
                <th style={{ padding: '0.75rem 1rem' }}>Address</th>
                <th style={{ padding: '0.75rem 1rem' }}>City</th>
              </tr>
            </thead>
            <tbody>
              {currentCustomers.map((c, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#2563eb' }}>{c.cd_no}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>{c.customer_group}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>{c.search_name}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>{c.customer_name}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>{c.update_sales_code}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>{c.update_after_sales_code}</td>
                  <td style={{ padding: '0.75rem 1rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.address}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>{c.city}</td>
                </tr>
              ))}
              {filteredCustomers.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    No customers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {filteredCustomers.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', backgroundColor: '#f8fafc', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', borderTop: '1px solid var(--color-border)' }}>
            <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCustomers.length)} of {filteredCustomers.length.toLocaleString()} results
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{ padding: '0.4rem 0.8rem', border: '1px solid #cbd5e1', borderRadius: '4px', backgroundColor: currentPage === 1 ? '#f1f5f9' : 'white', color: currentPage === 1 ? '#94a3b8' : '#334155', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: '0.85rem' }}
              >
                Previous
              </button>
              <div style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem', color: '#334155', fontWeight: '500' }}>
                Page {currentPage} of {totalPages}
              </div>
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={{ padding: '0.4rem 0.8rem', border: '1px solid #cbd5e1', borderRadius: '4px', backgroundColor: currentPage === totalPages ? '#f1f5f9' : 'white', color: currentPage === totalPages ? '#94a3b8' : '#334155', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: '0.85rem' }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Saved Google Maps Places Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: '1.5rem' }}>
        
        {/* Favorites */}
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <Star fill="#d97706" color="#d97706" />
            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.2rem', fontWeight: 700 }}>รายการโปรดจาก Google Maps ({favoritePlaces.length})</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '400px', overflowY: 'auto', paddingRight: '0.5rem' }}>
            {favoritePlaces.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>ยังไม่มีรายการโปรด</div>
            ) : (
              favoritePlaces.map(p => (
                <div key={p.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', backgroundColor: '#fffbf1' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#b45309' }}>{p.name}</h4>
                  <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', color: '#64748b', display: 'flex', gap: '0.25rem', alignItems: 'flex-start' }}>
                    <MapPin size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{p.address}</span>
                  </p>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
                    <span>บันทึกโดย: {p.saved_by_name} {isAdmin ? `(${p.saved_by_team})` : ''}</span>
                    <span>{new Date(p.created_at).toLocaleDateString('th-TH')}</span>
                  </div>
                  
                  {/* Action Buttons */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #fde68a' }}>
                    <button onClick={() => handleShowOnMap(p)} style={{ flex: 1, backgroundColor: 'white', color: '#3b82f6', border: '1px solid #bfdbfe', padding: '0.3rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.75rem' }}>
                      <MapIcon size={12} /> แผนที่
                    </button>
                    <button onClick={() => handleUpdatePlace(p.id, 'move_to_new_customer')} style={{ flex: 1, backgroundColor: 'white', color: '#059669', border: '1px solid #a7f3d0', padding: '0.3rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <ArrowRight size={12} /> Sales Lead
                      </div>
                    </button>
                    <button onClick={() => handleDeletePlace(p.id)} style={{ backgroundColor: '#fee2e2', color: '#ef4444', border: 'none', padding: '0.3rem 0.5rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* New Customers */}
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <UserPlus color="#059669" />
            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.2rem', fontWeight: 700 }}>รายชื่อ Sales Lead จาก Google Maps ({newCustomers.length})</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '400px', overflowY: 'auto', paddingRight: '0.5rem' }}>
            {newCustomers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>ยังไม่มีรายชื่อ Sales Lead</div>
            ) : (
              newCustomers.map(p => (
                <div key={p.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem', backgroundColor: '#f0fdf4' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#047857' }}>{p.name}</h4>
                  <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', color: '#64748b', display: 'flex', gap: '0.25rem', alignItems: 'flex-start' }}>
                    <MapPin size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{p.address}</span>
                  </p>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
                    <span>บันทึกโดย: {p.saved_by_name} {isAdmin ? `(${p.saved_by_team})` : ''}</span>
                    <span>{new Date(p.created_at).toLocaleDateString('th-TH')}</span>
                  </div>
                  
                  {/* Action Buttons */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #a7f3d0' }}>
                    <button onClick={() => handleShowOnMap(p)} style={{ flex: 1, backgroundColor: 'white', color: '#3b82f6', border: '1px solid #bfdbfe', padding: '0.3rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.75rem' }}>
                      <MapIcon size={12} /> แผนที่
                    </button>
                    <button onClick={() => handleUpdatePlace(p.id, 'plan_visit')} style={{ flex: 1, backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '0.3rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.75rem' }}>
                      <Car size={12} /> วางแผนไปเยี่ยม
                    </button>
                    <button onClick={() => handleDeletePlace(p.id)} style={{ backgroundColor: '#fee2e2', color: '#ef4444', border: 'none', padding: '0.3rem 0.5rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
